import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { addAuthorizedUser, removeAuthorizedUser } from '../../../utils/demoEnvMgmtApi';
import { errorMessage } from './error-message';
import ConfirmDeleteDialog from '../../../components/admin/ConfirmDeleteDialog';

function AuthorizedUsersSection({ users, onChanged, currentUserEmail }) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await addAuthorizedUser(email.trim());
      setEmail('');
      await onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await removeAuthorizedUser(deleteTarget.id);
      setDeleteTarget(null);
      await onChanged();
    } catch (err) {
      setError(errorMessage(err));
      setDeleteTarget(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="demo-env-mgmt-section">
      <h2 className="demo-env-mgmt-section-title">Authorized users</h2>
      <p className="demo-env-mgmt-label">
        Okta emails allowed to access this page.
      </p>

      <div className="demo-env-mgmt-columns" style={{ marginTop: 8 }}>
        <div className="demo-env-mgmt-column">
          <h3 className="demo-env-mgmt-column-title">Add authorized user</h3>
          <form onSubmit={handleAdd}>
            <div className="demo-env-mgmt-field">
              <label className="demo-env-mgmt-label" htmlFor="new-authorized-user-email">Email</label>
              <input
                id="new-authorized-user-email"
                className="demo-env-mgmt-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="demo-env-mgmt-field demo-env-mgmt-field-actions" style={{ marginTop: 8 }}>
              <button className="demo-env-mgmt-btn" type="submit" disabled={submitting}>
                {submitting ? 'Adding…' : 'Add'}
              </button>
            </div>
          </form>
          {error && <p className="demo-env-mgmt-error">{error}</p>}
        </div>

        <div className="demo-env-mgmt-column">
          <h3 className="demo-env-mgmt-column-title">Authorized users</h3>
          <div className="demo-env-mgmt-table-wrap">
            <table className="demo-env-mgmt-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th className="demo-env-mgmt-table-actions" />
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isCurrentUser = currentUserEmail && user.email === currentUserEmail;
                  return (
                    <tr key={user.id}>
                      <td>
                        {user.email}
                        {isCurrentUser ? ' (you)' : ''}
                      </td>
                      <td>{user.isAdmin ? 'Admin' : '—'}</td>
                      <td className="demo-env-mgmt-table-actions">
                        <button
                          type="button"
                          className="demo-env-mgmt-btn-delete"
                          onClick={() => setDeleteTarget(user)}
                          disabled={submitting}
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && (
                  <tr className="demo-env-mgmt-empty-row">
                    <td colSpan={3}>No authorized users yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {deleteTarget && (
        <ConfirmDeleteDialog
          entityType="authorized user"
          entityName={deleteTarget.email}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

export default AuthorizedUsersSection;
